import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import TickImage from "@assets/tick.svg";
import CrossImage from "@assets/cross.svg";
import { useNavigate } from "react-router-dom";
import type { RootState } from "../../../store/store";
import LabelledInput from "../labelledInput";
import Button from "../../bricks/button";
import {
  submitAlias,
  checkAliasAvailability
} from "../../../store/features/userSlice";
import hasBadWords from "../../../utils/profanity";
import {
  AliasMessageLabel,
  BackgroundContainer,
  OnBackgroundDiv
} from "./aliasModal.style";
import { hasNoLetter, hasSpecialChars } from "../../../utils/validationUtils";

const getMessage = (val: string): string => {
  let msg = "";

  if (hasBadWords(val)) msg = "Your alias contains profanity";

  if (val.length > 3 && val.length < 18) {
    if (hasNoLetter(val)) {
      msg = "Your alias must contain at least 1 letter.";
    }

    if (hasSpecialChars(val)) {
      return "Your alias can not contain special characters";
    }

    if (val.match(/\d/gm)?.length > 3) {
      msg = "Your alias cannot have more than 3 numbers";
    }
  } else if (val.length >= 18) {
    msg = "Your alias cannot be longer than 18 characters";
  } else {
    msg = "not enough chars";
  }

  return msg;
};

const AliasModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state: RootState) => state.user);
  const config = useSelector((state: RootState) => state.config.data);
  const [isInProgress, setIsInProgress] = useState<boolean>(false);
  const [aliasRequiredMessage, setAliasRequiredMessage] = useState<string>("");
  const aliasInput = useRef(null);
  const [toShowNameAvailable, setToShowNameAvailable] =
    useState<boolean>(false);
  const [toShowNameNotAvailable, setToShowNameNotAvailable] =
    useState<boolean>(false);
  const [toShowIncorrect, setToShowIncorrect] = useState<boolean>(false);
  const [canSubmitAlias, setCanSubmitAlias] = useState<boolean>(false);

  const [toShow, setToShow] = useState(true);

  const finalAlias = useRef<string>("");

  const onBackgroundClick = () => {
    setToShow(false);
    if (canSubmitAlias) setCanSubmitAlias(false);
    if (aliasRequiredMessage.length > 0) setAliasRequiredMessage("");
    if (toShowNameAvailable) setToShowNameAvailable(false);
  };

  const onAliasRequiredInput = (val: string) => {
    if (toShowIncorrect) setToShowIncorrect(false);
    if (toShowNameAvailable) setToShowNameAvailable(false);
    const onAvailabilityCheck = (response: any) => {
      if (!response.data) {
        // Competition name not available

        setToShowNameAvailable(false);
        setToShowNameNotAvailable(true);
      } else {
        finalAlias.current = val;
        setToShowNameAvailable(true);
        setCanSubmitAlias(response.data);
      }
    };

    let msg = getMessage(val);

    if (msg === "") {
      checkAliasAvailability({
        alias: val,
        callback: onAvailabilityCheck,
        config
      });
    } else {
      msg = msg === "not enough chars" ? "" : msg;
      setCanSubmitAlias(false);
    }

    setAliasRequiredMessage(msg);
    setToShowNameNotAvailable(false);
  };

  const onAliasSubmitClick = () => {
    setIsInProgress(true);
    setToShowNameNotAvailable(false);
    setToShowNameAvailable(false);
    aliasInput.current.text = "";
    dispatch(
      submitAlias({
        alias: finalAlias.current,
        loginType: "Hollywoodbets",
        userName: userData.userName
      })
    );
  };

  const onLearnHowToClick = () => {
    if (!isInProgress) navigate("/learntoplay");
  };

  useEffect(() => {
    if (userData.aliasRequired) {
      setToShow(true);
    } else {
      setIsInProgress(false);
    }
  }, [userData.aliasRequired]);

  useEffect(() => {
    if (userData.errorMessage.length > 1 && !toShowIncorrect) {
      setIsInProgress(false);
      setToShowIncorrect(true);
    }
  }, [userData.errorMessage]);

  return (
    userData.aliasRequired &&
    toShow && (
      <div
        style={{
          position: "absolute",
          zIndex: 80,
          width: "100%",
          height: "100%",
          fontFamily: "Poppins, sans-serif"
        }}>
        <div
          style={{
            position: "relative",
            background: "none",
            height: "100%",
            width: "100%"
          }}>
          <OnBackgroundDiv onClick={onBackgroundClick} />

          <BackgroundContainer>
            <>
              <span
                style={{
                  marginTop: "10px",
                  fontWeight: "bold",
                  color: "#764CBF"
                }}>
                Enter your competition name
              </span>
              <div
                style={{
                  width: "50%",
                  height: "2px",
                  borderBottom: "1px solid #e0e0eb"
                }}
              />
              <span
                style={{
                  marginTop: "8px",
                  fontWeight: "normal",
                  fontSize: "0.9rem",
                  color: "#86868d",
                  width: "92%",
                  height: "max-content",
                  placeItems: "center"
                }}>
                This will be your name visible to all players.
              </span>

              <LabelledInput
                width="92%"
                height="2.5rem"
                marginTop="5px"
                reference={aliasInput}
                isPassword={false}
                align="center"
                placeholder="Enter your competition name.."
                valueUpdater={onAliasRequiredInput}
              />

              <div
                style={{
                  display: "flex",
                  height: "30px",
                  columnGap: "5px",
                  alignItems: "center"
                }}>
                <span
                  style={{
                    marginTop: "10px",
                    fontWeight: "bold",
                    fontSize: "0.7rem",
                    color: "#ff0000"
                  }}>
                  {aliasRequiredMessage}
                </span>

                {toShowNameAvailable && !aliasRequiredMessage && (
                  <>
                    <img
                      src={TickImage}
                      style={{
                        marginLeft: "0px",
                        width: "20px",
                        height: "20px"
                      }}
                    />
                    <AliasMessageLabel>
                      This competition name is available.
                    </AliasMessageLabel>
                  </>
                )}
                {toShowNameNotAvailable && (
                  <>
                    <img
                      src={CrossImage}
                      style={{
                        marginLeft: "0px",
                        width: "20px",
                        height: "20px"
                      }}
                    />
                    <AliasMessageLabel>
                      This competition name is not available.
                    </AliasMessageLabel>
                  </>
                )}

                {toShowIncorrect && (
                  <span
                    style={{
                      marginTop: "10px",
                      fontWeight: "bold",
                      fontSize: "0.7rem",
                      color: "#ff0000"
                    }}>
                    {userData.error || userData.errorMessage}
                  </span>
                )}
              </div>

              <Button
                width="150px"
                height="40px"
                margintop="5px"
                gradient={{
                  colors: ["#764CBF", "#5C2D91"],
                  direction: "linear"
                }}
                onClick={canSubmitAlias ? onAliasSubmitClick : undefined}
                text="Submit"
                disabled={
                  !canSubmitAlias ||
                  isInProgress ||
                  aliasRequiredMessage.length > 0
                }
                fontSize="0.8rem"
              />
            </>

            <Button
              width="150px"
              height="40px"
              margintop="5px"
              border="1px solid #5C2D91"
              backgroundcolor="white"
              onClick={onLearnHowToClick}
              fontcolor="#5C2D91"
              fontSize="0.6rem"
              text="Learn How to Play"
            />
          </BackgroundContainer>
        </div>
      </div>
    )
  );
};

export default AliasModal;
